import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, Eye, EyeOff, Headphones, LoaderCircle, Save, XCircle } from 'lucide-react'
import { useEffect, useState, type FormEvent } from 'react'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Card } from '../../components/ui/card'
import { Input } from '../../components/ui/input'
import { useToast } from '../../components/ui/toast'
import { api } from '../../lib/api'
import { relativeTime } from '../../lib/format'

const initial = {
  enabled: false,
  apiBaseUrl: 'https://supporto.tutelapatrimonioscf.it/api',
  apiKey: '',
  mailboxId: 1,
  requestTimeoutMs: 15_000,
}

export function FreeScoutSettingsPanel() {
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const settings = useQuery({ queryKey: ['freescout-settings'], queryFn: api.freeScoutSettings })
  const [form, setForm] = useState(initial)
  const [showKey, setShowKey] = useState(false)

  useEffect(() => {
    if (!settings.data) return
    setForm({
      enabled: settings.data.enabled,
      apiBaseUrl: settings.data.apiBaseUrl,
      apiKey: '',
      mailboxId: settings.data.mailboxId,
      requestTimeoutMs: settings.data.requestTimeoutMs,
    })
  }, [settings.data])

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['freescout-settings'] })
    void queryClient.invalidateQueries({ queryKey: ['system-status'] })
  }
  const save = useMutation({
    mutationFn: api.saveFreeScoutSettings,
    onSuccess: () => {
      showToast('Configurazione FreeScout salvata')
      refresh()
    },
    onError: (error) =>
      showToast(error instanceof Error ? error.message : 'Salvataggio fallito', 'error'),
  })
  const test = useMutation({
    mutationFn: api.testFreeScout,
    onSuccess: (result) => {
      showToast(
        result.connected ? 'Connessione FreeScout verificata' : result.error || 'Test fallito',
        result.connected ? 'success' : 'error',
      )
      refresh()
    },
    onError: (error) => showToast(error instanceof Error ? error.message : 'Test fallito', 'error'),
  })
  const submit = (event: FormEvent) => {
    event.preventDefault()
    save.mutate({ ...form, apiKey: form.apiKey.trim() || undefined })
  }

  return (
    <Card className="mt-8 p-5 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="grid size-10 shrink-0 place-items-center rounded-xl bg-[var(--primary-soft)] text-[var(--primary)]">
            <Headphones className="size-5" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Ticket FreeScout</h2>
            <p className="mt-1 text-sm leading-6 text-[var(--muted-foreground)]">
              Inoltra le richieste non gestite con oggetto{' '}
              <span className="font-mono">[WA/PEMMY2]</span>.
            </p>
          </div>
        </div>
        <Badge
          className={
            settings.data?.connection.connected
              ? 'bg-[var(--success-soft)] text-[var(--success)]'
              : 'bg-[var(--muted)] text-[var(--muted-foreground)]'
          }
        >
          {settings.data?.connection.connected ? (
            <CheckCircle2 className="size-3.5" />
          ) : (
            <XCircle className="size-3.5" />
          )}
          {settings.data?.connection.connected ? 'Connesso' : 'Non verificato'}
        </Badge>
      </div>

      <form onSubmit={submit} className="mt-6 grid gap-5">
        <label className="flex items-center gap-3 rounded-xl border border-[var(--border)] p-4 text-sm font-semibold">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(event) => setForm((value) => ({ ...value, enabled: event.target.checked }))}
            className="size-4 accent-[var(--primary)]"
          />
          Attiva l’inoltro delle richieste non gestite
        </label>
        <div className="grid gap-5 lg:grid-cols-2">
          <label className="grid gap-2 text-sm font-semibold lg:col-span-2">
            API Base URL
            <Input
              value={form.apiBaseUrl}
              onChange={(event) =>
                setForm((value) => ({ ...value, apiBaseUrl: event.target.value }))
              }
            />
          </label>
          <label className="grid gap-2 text-sm font-semibold">
            API key
            <div className="relative">
              <Input
                type={showKey ? 'text' : 'password'}
                value={form.apiKey}
                placeholder={
                  settings.data?.apiKeyConfigured ? '••••••••••••••••' : 'Incolla la API key'
                }
                onChange={(event) => setForm((value) => ({ ...value, apiKey: event.target.value }))}
                className="pr-12"
              />
              <button
                type="button"
                aria-label={showKey ? 'Nascondi API key' : 'Mostra API key'}
                onClick={() => setShowKey((value) => !value)}
                className="absolute right-1 top-1 grid size-9 place-items-center rounded-lg text-[var(--muted-foreground)] hover:bg-[var(--muted)]"
              >
                {showKey ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </button>
            </div>
          </label>
          <label className="grid gap-2 text-sm font-semibold">
            Mailbox ID
            <Input
              type="number"
              min={1}
              value={form.mailboxId}
              onChange={(event) =>
                setForm((value) => ({ ...value, mailboxId: Number(event.target.value) }))
              }
            />
          </label>
          <label className="grid gap-2 text-sm font-semibold">
            Timeout richieste (ms)
            <Input
              type="number"
              min={1000}
              max={60000}
              step={1000}
              value={form.requestTimeoutMs}
              onChange={(event) =>
                setForm((value) => ({ ...value, requestTimeoutMs: Number(event.target.value) }))
              }
            />
          </label>
          <div className="flex flex-col justify-end text-sm text-[var(--muted-foreground)]">
            <span>Ultimo controllo: {relativeTime(settings.data?.connection.checkedAt)}</span>
            {settings.data?.connection.mailboxName && (
              <span>Mailbox: {settings.data.connection.mailboxName}</span>
            )}
          </div>
        </div>
        {settings.data?.connection.error && (
          <p className="rounded-xl bg-[var(--destructive-soft)] p-3 text-sm text-[var(--destructive)]">
            {settings.data.connection.error}
          </p>
        )}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            variant="secondary"
            disabled={!settings.data?.apiKeyConfigured || test.isPending || save.isPending}
            onClick={() => test.mutate()}
          >
            {test.isPending && <LoaderCircle className="size-4 animate-spin" />} Verifica
            connessione
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? (
              <LoaderCircle className="size-4 animate-spin" />
            ) : (
              <Save className="size-4" />
            )}{' '}
            Salva FreeScout
          </Button>
        </div>
      </form>
    </Card>
  )
}
